import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { FirebaseAnalytics } from "@ionic-native/firebase-analytics";
import { IonIcon } from "@ionic/react";
import { arrowForwardOutline } from "ionicons/icons";

import PageLayout from "../../layouts/PageLayout";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Subscription } from "model";
import Title from "components/Title";
import Button from "components/Button";
import api from "../../api";
import { tryCatch, getReasonString } from "../../utils";

import PlanSelectionForm from "../onboarding/components/PlanSelectionForm";
import {
  getDisplayablePlanPrices,
  type ConfigPrice,
} from "../onboarding/components/planSelectionPricing";

interface Props {
  reason?: string;
}

function isSamePlanAsSubscription(
  price: ConfigPrice,
  subscription: Subscription
): boolean {
  if (!price.recurring) return false;
  const intervalCount = Number(price.recurring.interval_count ?? 1);
  const subIntervalCount = Number(subscription.plan_interval_count ?? 1);
  return (
    price.product?.name === subscription.product_name &&
    price.recurring.interval === subscription.plan_interval &&
    intervalCount === subIntervalCount
  );
}

export const ManageSubContainer: React.FC<Props> = ({ reason }) => {
  const { adapterId } = useParams<{ adapterId: string }>();
  const history = useHistory();
  const { t } = useTranslation("manageSub");
  const { t: tOnboarding } = useTranslation("onboarding");
  const [prices, setPrices] = useState<ConfigPrice[]>([]);
  const { adapter, subscriptions } = useSelector((state: RootState) => ({
    adapter: state.adapter.adapter,
    subscriptions: state.adapter.subscriptions,
  }));
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const reasonString = getReasonString(reason);
  const [selectedSub, setSelectedSub] = useState<ConfigPrice | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<ConfigPrice | null>(null);

  /** Subscription changes must use Stripe recurring price IDs only (not one-time / prepaid). */
  const displayablePrices = useMemo(
    () => getDisplayablePlanPrices(prices, { recurringOnly: true, reason }),
    [prices, reason]
  );

  useEffect(() => {
    FirebaseAnalytics.logEvent("init_page", { page: "manage" });
    getConfiguration();
  }, []);

  useEffect(() => {
    if (adapterId) {
      const adapterSub = subscriptions.find(
        (sub: Subscription) => sub.adapter_id === adapterId
      );
      if (adapterSub) {
        setSubscription(adapterSub);
      }
    } else if (adapter && adapter.id) {
      const adapterSub = subscriptions.find(
        (sub: Subscription) => sub.adapter_id === adapter.id
      );
      if (adapterSub) {
        setSubscription(adapterSub);
      }
    }
  }, [adapterId, adapter, subscriptions]);

  const getConfiguration = async () => {
    const configRes = await tryCatch(api.subscription.getConfig());
    if (configRes.error) {
      console.warn(configRes.error.message);
    } else {
      if (configRes.result.data.prices) {
        setPrices(configRes.result.data.prices);
      }
    }
  };

  const handleUpdateSubscription = async (
    sub: Subscription,
    price_id: string
  ) => {
    if (!selectedSub?.recurring) {
      console.warn(
        "changePlan requires a recurring Stripe price id; refusing one-time price id"
      );
      return;
    }
    const apiResult = await tryCatch(
      api.subscription.changePlan(sub.subscription_id, price_id)
    );

    if (apiResult.error) {
      console.warn(apiResult.error.message);
    } else {
      if (sub.default_payment_method) {
        window.location.href = "/account?reason=plan_updated";
      } else {
        window.location.href = "/account?reason=plan_updated_no_payment_method";
      }
      console.log("changePlan returned " + apiResult.result.data.result);
    }
  };

  const currentPlanSelected =
    subscription &&
    selectedPrice &&
    isSamePlanAsSubscription(selectedPrice, subscription);

  const currentPriceId = useMemo(() => {
    if (!subscription) return null;
    const match = displayablePrices.find((price) =>
      isSamePlanAsSubscription(price, subscription)
    );
    return match?.id ?? null;
  }, [displayablePrices, subscription]);

  const handleSelectPrice = (price: ConfigPrice) => {
    if (currentPriceId && price.id === currentPriceId) return;
    setSelectedPrice(price);
  };

  return (
    <PageLayout back={history.goBack}>
      <Title title={t("title")} className="mb-2" />
      {reason && (
        <div
          style={{ padding: "20px", display: "flex", justifyContent: "center" }}
        >
          <p style={{ whiteSpace: "pre-line", width: "80%" }}>
            {t(reasonString)}
          </p>
        </div>
      )}
      {prices.length > 0 && subscription && !selectedSub && (
        <div>
          <div style={styles.formContainer}>
            <PlanSelectionForm
              prices={displayablePrices}
              allPricesForSavings={prices}
              selectedPrice={selectedPrice}
              onSelectPrice={handleSelectPrice}
              renewalOption="renewLater"
              showRenewalToggle={false}
              currentPriceId={currentPriceId}
            />
            <div style={styles.continueRow}>
              <Button
                data-testid="submit"
                className="text-center mr-3 w-full"
                size="small"
                type="button"
                style={{ maxWidth: "320px" }}
                onClick={() => {
                  if (selectedPrice?.recurring) {
                    setSelectedSub(selectedPrice);
                  }
                }}
                disabled={!selectedPrice || !!currentPlanSelected}
              >
                {currentPlanSelected ? t("yourCurrentPlan") : t("subscribe")}
              </Button>
            </div>
            {selectedPrice && (
              <div style={styles.billingAddendum}>
                <p>{tOnboarding("choosePlan.billingAddendum1")}</p>
                <p>{tOnboarding("choosePlan.billingAddendum2")}</p>
              </div>
            )}
          </div>
        </div>
      )}
      {selectedSub && subscription && (
        <div style={styles.formContainer}>
          <div style={{ alignSelf: "flex-start" }}>
            <Button
              data-testid="submit"
              className="text-center mr-3"
              size="small"
              type="button"
              disabled={false}
              onClick={() => {
                setSelectedSub(null);
              }}
            >
              Back
            </Button>
          </div>
          <Title title={t("confirmChangeSub")} className="mb-2" />
          <div>{t("followingChange")}</div>
          <div style={styles.productContainer}>
            <div style={styles.changeSubContainer}>
              <div style={styles.productTitle}>{subscription.product_name}</div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row" as "row",
                  gap: "10px",
                }}
              >
                <div style={styles.productPrice}>{`$${Number(
                  subscription.plan_amount / 100
                ).toFixed(2)}`}</div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column" as "column",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(26,26,26,0.5)",
                      fontSize: "12px",
                    }}
                  >
                    per{" "}
                    {(subscription as { plan_interval_count?: number })
                      .plan_interval_count === 2
                      ? t("per2Years")
                      : subscription.plan_interval === "year"
                      ? t("perYear")
                      : t("perMonth")}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <IonIcon
                icon={arrowForwardOutline}
                className="h-5 w-5 text-orange"
                aria-hidden="true"
              />
            </div>
            <div style={styles.changeSubContainer}>
              <div style={styles.productTitle}>
                {selectedSub.product?.name ?? ""}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row" as "row",
                  gap: "10px",
                }}
              >
                <div style={styles.productPrice}>{`$${Number(
                  selectedSub.unit_amount / 100
                ).toFixed(2)}`}</div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column" as "column",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(26,26,26,0.5)",
                      fontSize: "12px",
                    }}
                  >
                    per{" "}
                    {selectedSub.recurring &&
                    selectedSub.recurring.interval === "year" &&
                    selectedSub.recurring.interval_count === 2
                      ? t("per2Years")
                      : selectedSub.recurring?.interval === "year"
                      ? t("perYear")
                      : t("perMonth")}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              paddingBottom: "20px",
              width: "600px",
              lineHeight: "150%",
            }}
          >
            {subscription.product_name !== selectedSub.product?.name &&
            selectedSub.product?.name === "Privacy Premium"
              ? t("confirmationMessageProrate")
              : t("confirmationMessage")}
          </div>
          <div style={{ alignSelf: "center" }}>
            <Button
              data-testid="submit"
              className="text-center mr-3"
              size="small"
              type="button"
              disabled={false}
              onClick={() => {
                handleUpdateSubscription(subscription, selectedSub.id);
              }}
            >
              Change Subscription
            </Button>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

const styles = {
  formContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    gap: "20px",
  },
  continueRow: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "8px",
  },
  billingAddendum: {
    width: "100%",
    maxWidth: "92%",
    marginTop: "8px",
    fontSize: "13px",
    color: "rgba(26,26,26,0.75)",
    lineHeight: 1.5,
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
  productContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row" as "row",
    flexWrap: "wrap" as "wrap",
    justifyContent: "center",
    gap: "10px",
  },
  productTitle: {
    fontSize: "18px",
    fontWeight: 500,
    margin: "5px",
    width: "100%",
    marginBottom: "10px",
  },
  productPrice: {
    fontWeight: 700,
    fontSize: "32px",
  },
  changeSubContainer: {
    border: "1px solid #d3d3d3",
    borderRadius: "3px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "10px",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
  },
};

export default ManageSubContainer;
